<template>
  <div class="cr-flex cr-flex-col gap-3 cr-w-100">
    <template v-if="!isHistoryLoading">
      <SearchInput
        class="copilot-history__search-input"
        :input-data-testid="'pantry-product-search'"
        placeholder="Search chats"
        :value="searchTerm"
        @update:value="searchTerm = $event"
      />
      <div
        v-if="threadsMatchingSearch.length > 0"
        class="cr-h-100 cr-flex cr-flex-col cr-w-100 cr-overflow-auto spc-pt-2"
      >
        <div
          v-for="[date, threadGroup] in Object.entries(threadsGroupedByDate)"
          :key="date"
          class="cr-flex cr-flex-col gap-1"
        >
          <span class="label-s-medium pepper-700">{{ date }}</span>
          <ul class="thread-group-list-container spc-pl-2">
            <li
              v-for="thread in threadGroup"
              :key="thread.id"
              class="thread-item cr-cursor-pointer label-m-regular spc-py-1 spc-pl-1"
              :data-testid="`crafty-copilot-thread-${parameterize(thread.subject)}`"
              @click="emitThreadSelected(thread)"
            >
              {{ thread.subject }}
            </li>
          </ul>
        </div>
        <span class="label-s-medium pepper-700">Only chats from the last 90 days are shown.</span>
      </div>
      <EmptyStateCard
        v-else
        body="Try a different search."
        class="cr-flex cr-flex-col cr-align-center"
        title="No chats found!"
      />
    </template>
    <template v-else>
      <CraftyCopilotHistoryLoading />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { Thread, ThreadPage } from './CraftyCopilot.vue'
import SearchInput from './SearchInput.vue'
import { stringSearch } from '../utils/modules/string_search'
import EmptyStateCard from './EmptyStateCard.vue'
import CraftyCopilotHistoryLoading from './CraftyCopilotHistoryLoading.vue'

export default defineComponent({
  name: 'CraftyCopilotHistory',
  components: {
    SearchInput,
    EmptyStateCard,
    CraftyCopilotHistoryLoading,
  },
  props: {
    isHistoryLoading: {
      type: Boolean,
      default: false,
    },
    threads: {
      type: Array as () => Array<Thread>,
      default: () => [],
    },
  },
  emits: ['select-thread'],
  data() {
    return {
      searchTerm: '',
    }
  },
  computed: {
    threadsGroupedByDate() {
      return this.threadsMatchingSearch.reduce((threadGroups: Record<string, Thread[]>, thread: Thread) => {
        let date = new Date(thread.updated_at).toLocaleDateString()

        const today = new Date().toLocaleDateString()
        const isToday = date === today
        if (isToday)
          date = 'Today'

        if (!threadGroups[date])
          threadGroups[date] = []

        threadGroups[date].push(thread)
        return threadGroups
      }, {})
    },
    threadsMatchingSearch(): Thread[] {
      if (!this.searchTerm) return this.threads

      return stringSearch(this.threads, 'subject', this.searchTerm)
    },
  },
  methods: {
    emitThreadSelected(thread: Thread) {
      this.$emit('select-thread', { threadId: thread.id, title: thread.subject } as ThreadPage)
    },
    parameterize(text: string): string {
      return text.replace(/\s+/g, '-').toLowerCase()
    },
  },
})
</script>

<style lang="scss" scoped>
.copilot-history {
  &__search-input {
    height: 36px;
  }
}

:deep(.search-input__inner) {
  height: 18px;
}

.pepper-700 {
  color: var(--pepper-700)
}

.thread-group-list-container {
  list-style-type: none;
}

.thread-item {
  border-radius: var(--cr-br-xs);

  &:hover {
    background-color: var(--grape-jelly-100);
  }
}
</style>
